// frontend/src/features/livestock/SharedLinksSection.tsx
// Shared links (design 8.6; livestock compare design 8.3): on the Tank Shots
// page the tank's live tank shot links, on an organism's page that organism's
// live links. Each with its two dates ("photo removed" for a deleted photo),
// title, Copy, and Stop sharing behind a short confirm; Stop sharing on all
// tanks (every link of the account, both kinds) behind its own. Nothing shows
// while there are no links.

import { useMemo, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Collapse,
  Flex,
  Group,
  Modal,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconCopy,
  IconLink,
} from '@tabler/icons-react';
import { useRevokeAllShareLinks, useRevokeShareLink, useShareLinks } from '@/hooks/useShareLinks';
import type { ShareLink } from '@/types/shareLinks';
import { copyLink, linkDates } from './shareLinkActions';

export interface SharedLinksSectionProps {
  tankId: string;
  /** An organism's page: its own links only. Without it, the tank's tank shot links. */
  livestockId?: number;
}

/** The links this page lists: one organism's, or the tank shots' (the list holds both kinds). */
export function linksFor(links: ShareLink[], livestockId: number | undefined): ShareLink[] {
  return links.filter((link) =>
    livestockId === undefined
      ? link.kind === 'tank_compare'
      : link.kind === 'organism_compare' && link.target.livestock_id === livestockId
  );
}

/** What a row says the link shows besides its photos: " · shows tank name" and the like. */
function showsWhat(link: ShareLink): string {
  if (link.kind === 'tank_compare') return link.show_tank_name ? ' · shows tank name' : '';
  return (
    (link.show_subject_name ? ' · shows name' : '') +
    (link.show_scientific_name ? ' · shows scientific name' : '')
  );
}

type Confirming = { kind: 'one'; link: ShareLink } | { kind: 'all' } | null;

function LinkRow({
  link,
  copied,
  onCopy,
  onStop,
}: {
  link: ShareLink;
  copied: boolean;
  onCopy: () => void;
  onStop: () => void;
}): JSX.Element {
  const broken = link.before_date === null || link.after_date === null;
  return (
    <Card withBorder radius="md" p="sm" data-testid="shared-link">
      {/* Stacked on a phone, actions to the right from sm up. */}
      <Flex
        direction={{ base: 'column', sm: 'row' }}
        justify="space-between"
        align={{ base: 'stretch', sm: 'center' }}
        gap={{ base: 4, sm: 'sm' }}
      >
        <Stack gap={2} miw={0} style={{ flex: 1 }}>
          <Text size="sm" fw={500} style={{ fontVariantNumeric: 'tabular-nums' }}>
            {linkDates(link.before_date, link.after_date)}
          </Text>
          <Text
            size="sm"
            c={link.title ? undefined : 'dimmed'}
            lineClamp={2}
            style={{ overflowWrap: 'anywhere' }}
          >
            {link.title ?? 'No title'}
            {showsWhat(link)}
          </Text>
          {broken && (
            <Group gap={6}>
              <Badge
                variant="light"
                color="coral"
                size="xs"
                tt="uppercase"
                style={{ fontSize: 10, letterSpacing: '0.05em', fontWeight: 600 }}
              >
                Not working
              </Badge>
              <Text size="xs" c="dimmed">
                A photo in it was deleted
              </Text>
            </Group>
          )}
        </Stack>
        <Group gap={4} wrap="nowrap" justify="flex-end">
          {/* A broken link only says "no longer shared": nothing to copy. */}
          {!broken && (
            <Tooltip label={copied ? 'Copied' : 'Copy link'}>
              <ActionIcon
                variant="subtle"
                color="gray"
                size={44}
                onClick={onCopy}
                aria-label={copied ? 'Copied' : 'Copy link'}
              >
                {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              </ActionIcon>
            </Tooltip>
          )}
          <Button variant="subtle" color="red" size="compact-sm" h={44} onClick={onStop}>
            Stop sharing
          </Button>
        </Group>
      </Flex>
      {!broken && (
        // Selectable, for when the clipboard refuses.
        <TextInput
          mt="xs"
          size="sm"
          value={link.url}
          readOnly
          aria-label="Link"
          onFocus={(event) => event.currentTarget.select()}
          styles={{ input: { minHeight: 44, color: 'var(--mantine-color-dimmed)' } }}
        />
      )}
    </Card>
  );
}

export function SharedLinksSection({
  tankId,
  livestockId,
}: SharedLinksSectionProps): JSX.Element | null {
  const links = useShareLinks(tankId);
  const revoke = useRevokeShareLink(tankId);
  const revokeAll = useRevokeAllShareLinks();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState<Confirming>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const items = useMemo(() => linksFor(links.data ?? [], livestockId), [links.data, livestockId]);

  if (links.isError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="red" title="Shared links didn't load">
        <Button size="compact-sm" variant="light" color="red" onClick={() => void links.refetch()}>
          Try again
        </Button>
      </Alert>
    );
  }
  if (items.length === 0) return null;

  const copy = async (link: ShareLink): Promise<void> => {
    const ok = await copyLink(link.url);
    setCopiedId(ok ? link.id : null);
    setNotice(ok ? null : "Couldn't copy: select the link under it and copy it.");
  };

  const stop = (): void => {
    if (!confirming) return;
    const done = {
      onSuccess: (): void => {
        setConfirming(null);
        setNotice(null);
      },
      onError: (): void => {
        setConfirming(null);
        setNotice("Sharing wasn't stopped. Try again in a moment.");
      },
    };
    if (confirming.kind === 'one') revoke.mutate(confirming.link.id, done);
    else revokeAll.mutate(undefined, done);
  };
  const pending = revoke.isPending || revokeAll.isPending;
  // The account-wide stop. On an organism's page it says plainly that it reaches
  // beyond this organism (tank shots and every other organism's links too).
  const allLabel =
    livestockId === undefined ? 'Stop sharing on all tanks' : 'Stop sharing every link';
  const allDetail =
    livestockId === undefined
      ? 'Every shared link on all your tanks stops working at once.'
      : "Every shared link on your account stops working at once: this organism's, other organisms' and tank shots', on all your tanks.";

  return (
    <Card withBorder radius="xl" p={{ base: 'sm', md: 'lg' }} data-testid="shared-links">
      <Group justify="flex-start" wrap="nowrap" gap="xs">
        <Button
          variant="subtle"
          color="dark"
          px={4}
          h={44}
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          leftSection={<IconLink size={16} />}
          rightSection={open ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
        >
          <Text
            fw={600}
            style={{ fontFamily: 'Space Grotesk, sans-serif', fontVariantNumeric: 'tabular-nums' }}
          >
            Shared links · {items.length}
          </Text>
        </Button>
      </Group>

      <Collapse in={open}>
        <Stack gap="xs" mt="sm">
          {notice && (
            <Text size="sm" c="red" role="alert">
              {notice}
            </Text>
          )}
          {items.map((link) => (
            <LinkRow
              key={link.id}
              link={link}
              copied={copiedId === link.id}
              onCopy={() => void copy(link)}
              onStop={() => setConfirming({ kind: 'one', link })}
            />
          ))}
          <Group justify="space-between" align="center" gap="xs">
            <Text size="xs" c="dimmed" style={{ flex: '1 1 200px' }}>
              {livestockId === undefined
                ? 'Anyone with a link can see its two photos and their dates until you stop sharing.'
                : 'Links for this organism. Stop sharing every link stops all your links, on every tank and organism.'}
            </Text>
            <Button
              variant="subtle"
              color="red"
              size="compact-sm"
              h={44}
              onClick={() => setConfirming({ kind: 'all' })}
            >
              {allLabel}
            </Button>
          </Group>
        </Stack>
      </Collapse>

      <Modal
        opened={confirming !== null}
        onClose={() => setConfirming(null)}
        title={confirming?.kind === 'all' ? 'Stop sharing every link?' : 'Stop sharing this link?'}
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm">
            {confirming?.kind === 'all'
              ? allDetail
              : 'Anyone who opens it will see that it is no longer shared.'}{' '}
            Previews that messengers and forums have already made can&apos;t be recalled.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button
              variant="subtle"
              color="gray"
              onClick={() => setConfirming(null)}
              disabled={pending}
            >
              Cancel
            </Button>
            <Button color="red" onClick={stop} loading={pending}>
              {confirming?.kind === 'all' ? allLabel : 'Stop sharing'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
